import CloseRounded from "@mui/icons-material/CloseRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import {
  Box,
  Button,
  Badge,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  Typography,
} from "@mui/material";
import { useEffect, useState, type ReactNode } from "react";
import type { MeasurementSystem, SearchParams } from "@bw/shared/api";
import { getGoogleMapsApiKey } from "@bw/shared/components";
import type { NearbyCenter } from "../hooks/useNearbyCenter";
import { distanceOptions, radiusLabel } from "../distanceUnits";
import { MaterialAreaPicker } from "./MaterialAreaPicker";
import { NearbyControl } from "./NearbyControl";

type Service = NonNullable<SearchParams["facility_service"]>;

const activities: Array<{ value: Service | ""; label: string; chip: string }> = [
  { value: "", label: "All facilities", chip: "All" },
  { value: "court_reservation", label: "Court Reservation", chip: "Courts" },
  { value: "open_play", label: "Drop-in", chip: "Drop-in" },
  { value: "classes", label: "Classes & clinics", chip: "Classes" },
  { value: "coaching", label: "Private lessons", chip: "Lessons" },
  { value: "memberships", label: "Memberships", chip: "Memberships" },
];

interface FacilityFilterBarProps {
  /** The search field, shown beside the filters icon. */
  searchSlot?: ReactNode;
  coordinateSearchAllowed?: boolean;
  trailingAction?: ReactNode;
  service?: Service;
  city: string;
  center: NearbyCenter | null;
  hasLocation: boolean;
  radiusKm: number;
  openNow: boolean;
  specialOffers?: boolean;
  minCourts?: number;
  measurementSystem: MeasurementSystem;
  onServiceChange: (service: Service | "") => void;
  onCityChange: (city: string) => void;
  onRadiusChange: (km: number) => void;
  onOpenNowChange: (open: boolean) => void;
  onSpecialOffersChange?: (selected: boolean) => void;
  onMinCourtsChange?: (count: number) => void;
  onCenterChange: (center: NearbyCenter) => void;
  onClearLocation: () => void;
  onClearAll: () => void;
}

export function FacilityFilterBar({
  searchSlot,
  coordinateSearchAllowed = true,
  trailingAction,
  service,
  city,
  center,
  hasLocation,
  radiusKm,
  openNow,
  specialOffers = false,
  minCourts = 0,
  measurementSystem,
  onServiceChange,
  onCityChange,
  onRadiusChange,
  onOpenNowChange,
  onSpecialOffersChange,
  onMinCourtsChange,
  onCenterChange,
  onClearLocation,
  onClearAll,
}: FacilityFilterBarProps) {
  const [open, setOpen] = useState(false);
  const addressSearchAvailable =
    coordinateSearchAllowed && Boolean(getGoogleMapsApiKey());
  const [cityDraft, setCityDraft] = useState(city);
  const [areaDraft, setAreaDraft] = useState(center?.label ?? "");
  const [locationAttempt, setLocationAttempt] = useState(0);

  useEffect(() => setCityDraft(city), [city]);
  useEffect(() => setAreaDraft(center?.label ?? city), [center?.label, city]);
  const locationDraft = (addressSearchAvailable ? areaDraft : cityDraft).trim();
  const locationDraftChanged = locationDraft !== (center?.label ?? city);

  const clearLocation = () => {
    setLocationAttempt((value) => value + 1);
    setCityDraft("");
    setAreaDraft("");
    onClearLocation();
  };
  const clearAll = () => {
    setLocationAttempt((value) => value + 1);
    setCityDraft("");
    setAreaDraft("");
    onClearAll();
  };
  const applyCity = () => {
    setLocationAttempt((value) => value + 1);
    if (locationDraft && locationDraftChanged) {
      onCityChange(locationDraft);
      return;
    }
    if (center) return;
    onCityChange(locationDraft);
  };
  const activeCount =
    Number(Boolean(service)) +
    Number(openNow) +
    Number(specialOffers) +
    Number(minCourts > 0) +
    Number(Boolean(city || center));

  const activityControls = (
    <Box
      role="group"
      aria-label="Activity"
      sx={{
        display: "flex",
        gap: 1,
        overflowX: "auto",
        overscrollBehaviorX: "contain",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {activities.flatMap((item) => {
        const selected = service === item.value || (!service && !item.value);
        const chip = (
          <Chip
            key={item.value}
            label={item.chip}
            clickable
            size="small"
            variant={selected ? "filled" : "outlined"}
            color={selected ? "primary" : "default"}
            aria-pressed={selected}
            onClick={() => onServiceChange(item.value)}
            sx={{ flexShrink: 0, borderRadius: 99, px: 0.5 }}
          />
        );
        // Offers sits in the same row as a toggle, after Classes.
        return item.value === "classes" && onSpecialOffersChange
          ? [
              chip,
              <Chip
                key="offers"
                label="Offers"
                clickable
                size="small"
                variant={specialOffers ? "filled" : "outlined"}
                color={specialOffers ? "primary" : "default"}
                aria-pressed={specialOffers}
                onClick={() => onSpecialOffersChange(!specialOffers)}
                sx={{ flexShrink: 0, borderRadius: 99, px: 0.5 }}
              />,
            ]
          : [chip];
      })}
    </Box>
  );

  const filterFields = (
    <Stack spacing={3} sx={{ pt: 1 }}>

      <Box>
        <Typography
          component="h2"
          variant="subtitle1"
          sx={{ mb: 1, fontWeight: 800 }}
        >
          Location
        </Typography>
        {addressSearchAvailable ? (
          <MaterialAreaPicker
            label="City, ZIP code, or area"
            placeholder="Milpitas, CA 95035"
            value={areaDraft}
            onValueChange={setAreaDraft}
            onAreaSelect={(area) => {
              setCityDraft("");
              setAreaDraft(area.label);
              onCenterChange({
                latitude: area.lat,
                longitude: area.lng,
                label: area.label,
                coarse: true,
              });
            }}
          />
        ) : null}
        {!addressSearchAvailable ? (
          <Box
            component="form"
            aria-label="Filter facilities by city"
            onSubmit={(event) => {
              event.preventDefault();
              applyCity();
            }}
          >
            <TextField
              label="City, ZIP code, or area"
              size="small"
              value={cityDraft}
              onChange={(event) => {
                setLocationAttempt((value) => value + 1);
                setCityDraft(event.target.value);
              }}
              fullWidth
            />
          </Box>
        ) : null}
      </Box>

      <NearbyControl
        key={locationAttempt}
        center={center}
        hasLocation={hasLocation}
        onCenterChange={(next) => {
          setCityDraft("");
          onCenterChange(next);
        }}
        onClearLocation={clearLocation}
        locationCopy={
          center
            ? `Within ${radiusLabel(radiusKm, measurementSystem)} of ${center.label ?? "your location"}.`
            : "Use your current location instead."
        }
      />

      <TextField
        select
        size="small"
        label="Distance"
        value={center ? radiusKm : ""}
        disabled={!center}
        onChange={(event) => onRadiusChange(Number(event.target.value))}
        helperText={!center ? "Choose a location first." : undefined}
      >
        <MenuItem value="" disabled>
          Choose location
        </MenuItem>
        {distanceOptions(radiusKm, measurementSystem).map((choice) => (
          <MenuItem key={choice.value} value={choice.value}>
            Within {choice.label}
          </MenuItem>
        ))}
      </TextField>

      {onMinCourtsChange ? (
        <TextField
          select
          size="small"
          label="Minimum courts"
          value={minCourts}
          onChange={(event) => onMinCourtsChange(Number(event.target.value))}
        >
          <MenuItem value={0}>Any number</MenuItem>
          <MenuItem value={1}>1 or more</MenuItem>
          <MenuItem value={4}>4 or more</MenuItem>
          <MenuItem value={8}>8 or more</MenuItem>
          <MenuItem value={12}>12 or more</MenuItem>
        </TextField>
      ) : null}

      <ToggleButton
        value="open_now"
        selected={openNow}
        onChange={() => onOpenNowChange(!openNow)}
        aria-pressed={openNow}
        size="small"
        sx={{ justifyContent: "center", textTransform: "none" }}
      >
        Open now
      </ToggleButton>

    </Stack>
  );

  const filterCount = activeCount - Number(Boolean(service)) - Number(specialOffers);
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        {searchSlot ? <Box sx={{ flex: 1, minWidth: 0 }}>{searchSlot}</Box> : null}
        <IconButton
          aria-label={filterCount ? `Filters, ${filterCount} active` : "Filters"}
          onClick={() => setOpen(true)}
          sx={{ flexShrink: 0, color: filterCount ? "primary.main" : "text.secondary" }}
        >
          <Badge color="primary" badgeContent={filterCount} invisible={!filterCount}>
            <TuneRounded />
          </Badge>
        </IconButton>
      </Stack>
      {activityControls}
      {trailingAction}
      {city || center || openNow ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}>
          {city || center ? (
            <Chip
              size="small"
              variant="outlined"
              label={
                city ||
                `${center?.label ?? "Near me"} · ${radiusLabel(radiusKm, measurementSystem)}`
              }
              onDelete={clearLocation}
              sx={{ borderRadius: 99 }}
            />
          ) : null}
          {openNow ? (
            <Chip
              size="small"
              variant="outlined"
              label="Open now"
              onDelete={() => onOpenNowChange(false)}
              sx={{ borderRadius: 99 }}
            />
          ) : null}
          <Button size="small" color="inherit" onClick={clearAll}>
            Clear
          </Button>
        </Stack>
      ) : null}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        keepMounted
        fullWidth
        maxWidth="sm"
        aria-labelledby="facility-filters-title"
        sx={{
          "& .MuiDialog-container": {
            alignItems: { xs: "flex-end", sm: "center" },
          },
          "& .MuiDialog-paper": {
            m: { xs: 0, sm: 3 },
            width: { xs: "100%", sm: "calc(100% - 48px)" },
            borderRadius: { xs: "20px 20px 0 0", sm: 2 },
            maxHeight: "85dvh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography component="span" id="facility-filters-title">
            Facility filters
          </Typography>
          <IconButton aria-label="Close filters" onClick={() => setOpen(false)}>
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent>{filterFields}</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" color="inherit" onClick={clearAll}>
            Clear all
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              if (locationDraftChanged) applyCity();
              setOpen(false);
            }}
          >
            Show results
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
